import { FC, ReactNode, useContext, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { validateToken } from '../../utils/auth';
import { UserContext } from '@/contexts/UserContext';
import { refreshSession } from '@/data/authSession';
import { CircularProgress, Box } from '@mui/material';

interface AuthGuardProps {
  children: ReactNode;
}

export const AuthGuard: FC<AuthGuardProps> = (props) => {
  const { children } = props;
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const { isLoading, fetchUserData } = useContext(UserContext);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const accessToken = localStorage.getItem('access_token');
        const refreshToken = localStorage.getItem('refresh_token');

        if (!accessToken && !refreshToken) {
          setIsAuthenticated(false);
          setAuthCheckComplete(true);
          return;
        }

        if (accessToken && (refreshToken || validateToken(accessToken))) {
          // Süreye cihaz saati değil sunucu karar verir: profil istenir,
          // anahtarın süresi gerçekten dolmuşsa 401 ortak yenilemeyi başlatır.
          setIsAuthenticated(true);
          await fetchUserData();
        } else if (refreshToken) {
          // Erişim anahtarı yok: önce ortak yenileme, sonra profil. 401 veya
          // 403 anahtarları siler ve girişe döner; geçici hatada oturum
          // korunur, sonraki istek yeniden dener.
          try {
            await refreshSession(accessToken);
            setIsAuthenticated(true);
            await fetchUserData();
          } catch {
            setIsAuthenticated(Boolean(localStorage.getItem('refresh_token')));
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthCheckComplete(true);
      }
    };

    checkAuth();
  }, [location.pathname, fetchUserData]);

  if (!authCheckComplete || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, redirect to login page
  if (!isAuthenticated) {
    // Save the location they were trying to access for redirecting after login
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // If authenticated, show the protected content
  return <>{children}</>;
};
